/**
 * usePermissions Hook
 * Provides per-agency permission checking with two modes:
 * - "Any agency" mode: can/canAny/canAll — for nav items, route guards, pre-computed flags
 * - "Specific agency" mode: canInAgency/canAnyInAgency/canAllInAgency — for page-level checks
 */

import { useMemo, useCallback } from 'react';
import { useOathAuth } from '../services/oathAuthContext';
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasPermissionInAnyAgency,
  hasAnyPermissionInAnyAgency,
  hasAllPermissionsAcrossAgencies,
  getAgencyPermissions,
  normalizePermissions,
  canRequestTransferBonds,
  canPostTransferBonds,
  getRequestingAgencies,
  getPostingAgencies,
} from '../models/userModels';

/**
 * Hook for checking user permissions across agencies
 * @returns {PermissionsHookResult}
 */
export const usePermissions = () => {
  const { user } = useOathAuth();

  // Per-agency permissions dictionary: { agencyId: OathPermission[] }
  const permissionsDict = useMemo(() => {
    const perms = user?.permissions;
    if (!perms) return {};
    // Safety: normalize if still a flat array (stale localStorage)
    if (Array.isArray(perms)) return normalizePermissions(perms, user?.agencies);
    return perms;
  }, [user]);

  // ============================================
  // "Any agency" mode — for nav, route guards
  // ============================================

  /**
   * Check if ANY agency has a specific permission
   * @param {string} slug - Permission slug to check
   * @returns {boolean}
   */
  const can = useCallback(
    (slug) => hasPermissionInAnyAgency(permissionsDict, slug),
    [permissionsDict]
  );

  /**
   * Check if ANY agency has any of the specified permissions
   * @param {string[]} slugs - Permission slugs to check
   * @returns {boolean}
   */
  const canAny = useCallback(
    (slugs) => hasAnyPermissionInAnyAgency(permissionsDict, slugs),
    [permissionsDict]
  );

  /**
   * Check if each slug exists in at least one agency.
   * NOTE: Slugs can be satisfied by DIFFERENT agencies
   * (slug A in agency 1, slug B in agency 2 = pass).
   * This is correct for route guards — user can access the feature from some agency.
   * @param {string[]} slugs - Permission slugs to check
   * @returns {boolean}
   */
  const canAll = useCallback(
    (slugs) => hasAllPermissionsAcrossAgencies(permissionsDict, slugs),
    [permissionsDict]
  );

  // ============================================
  // "Specific agency" mode — for page-level checks
  // ============================================

  /**
   * Check if a specific agency has a permission
   * @param {string|number} agencyId
   * @param {string} slug
   * @returns {boolean}
   */
  const canInAgency = useCallback(
    (agencyId, slug) => hasPermission(getAgencyPermissions(permissionsDict, agencyId), slug),
    [permissionsDict]
  );

  /**
   * Check if a specific agency has any of the specified permissions
   * @param {string|number} agencyId
   * @param {string[]} slugs
   * @returns {boolean}
   */
  const canAnyInAgency = useCallback(
    (agencyId, slugs) => hasAnyPermission(getAgencyPermissions(permissionsDict, agencyId), slugs),
    [permissionsDict]
  );

  /**
   * Check if a specific agency has all of the specified permissions
   * @param {string|number} agencyId
   * @param {string[]} slugs
   * @returns {boolean}
   */
  const canAllInAgency = useCallback(
    (agencyId, slugs) => hasAllPermissions(getAgencyPermissions(permissionsDict, agencyId), slugs),
    [permissionsDict]
  );

  // ============================================
  // Pre-computed feature access flags ("any agency" mode)
  // ============================================

  const canAccessPortal = useMemo(() => can('READ:PORTAL'), [can]);
  const canAccessDashboard = useMemo(() => can('READ:DASHBOARD'), [can]);
  const canAccessTransferBonds = useMemo(() => canAny(['CREATE:TRANSFER-BOND:REQUEST', 'UPDATE:TRANSFER-BOND:POST']), [canAny]);
  const canAccessLargeBonds = useMemo(() => can('CREATE:LARGE-BOND:REQUEST'), [can]);
  const canAccessPowerReporting = useMemo(() => can('READ:EXECUTION-REPORT'), [can]);
  const canAccessPowerLog = useMemo(() => can('READ:POWER-LOG'), [can]);
  const canAccessPowerInquiry = useMemo(() => can('READ:POWERS'), [can]);
  const canAccessPowerInventory = useMemo(() => can('READ:POWER-REASSIGNMENT'), [can]);
  const canAccessForms = useMemo(() => can('READ:FORMS'), [can]);
  const canAccessDischarges = useMemo(() => can('UPDATE:POWER:DISCHARGE-REINSTATE'), [can]);
  const canAccessReports = useMemo(() => can('READ:REPORT-CENTER'), [can]);
  const canAccessAdmin = useMemo(() => canAny(['UPDATE:USERS', 'UPDATE:AGENCY:PROFILE', 'READ:USERS', 'UPDATE:AGENCY:PAYMENT-ACCOUNT']), [canAny]);
  const canAccessSubAgencyAR = useMemo(() => can('READ:SUB-AGENCIES'), [can]);
  const isAdmin = useMemo(() => can('UPDATE:USERS'), [can]);
  const canAccessAR = useMemo(() => can('READ:AR'), [can]);
  const canAccessElectronicPayment = useMemo(() => can('READ:ELECTRONIC-PAYMENT'), [can]);
  const canAccessContract = useMemo(() => can('READ:CONTRACT'), [can]);
  const canAccessVariableRate = useMemo(() => can('READ:VARIABLE-RATE'), [can]);
  const canViewUsers = useMemo(() => can('READ:USERS'), [can]);

  // ============================================
  // Agency capabilities (DEPRECATED — use slug-based checks)
  // ============================================

  const agencies = useMemo(() => user?.agencies || [], [user]);

  /** @deprecated Use canAny(['CREATE:TRANSFER-BOND:REQUEST']) instead */
  const canRequest = useMemo(() => canRequestTransferBonds(agencies), [agencies]);
  /** @deprecated Use canAny(['UPDATE:TRANSFER-BOND:POST']) instead */
  const canPost = useMemo(() => canPostTransferBonds(agencies), [agencies]);
  /** @deprecated Use permission slugs instead */
  const requestingAgencies = useMemo(() => getRequestingAgencies(agencies), [agencies]);
  /** @deprecated Use permission slugs instead */
  const postingAgencies = useMemo(() => getPostingAgencies(agencies), [agencies]);

  return {
    // Raw data
    permissionsDict,
    agencies,

    // "Any agency" permission check functions
    can,
    canAny,
    canAll,

    // "Specific agency" permission check functions
    canInAgency,
    canAnyInAgency,
    canAllInAgency,

    // Pre-computed feature access (all return true until Phase 4)
    canAccessPortal,
    canAccessDashboard,
    canAccessTransferBonds,
    canAccessLargeBonds,
    canAccessPowerReporting,
    canAccessPowerLog,
    canAccessPowerInquiry,
    canAccessPowerInventory,
    canAccessForms,
    canAccessDischarges,
    canAccessReports,
    canAccessAdmin,
    canAccessSubAgencyAR,
    isAdmin,
    canAccessAR,
    canAccessElectronicPayment,
    canAccessContract,
    canAccessVariableRate,
    canViewUsers,

    // Transfer bond agency capabilities (DEPRECATED)
    canRequest,
    canPost,
    requestingAgencies,
    postingAgencies,
  };
};

export default usePermissions;
